import React, { Component } from "react";
import { toast } from "react-toastify";
import API from "../utils/api";

class AdminTargetsPage extends Component {
    state = {
        executives: [],
        targets: [],
        futureTargets: [],
        loading: true,
        // Global default form
        globalPosted: "",
        globalAttempted: "",
        globalEffective: new Date().toISOString().slice(0, 10),
        savingGlobal: false,
        // Per-user form state: { [userId]: { posted, attempted, effective, saving } }
        userForms: {},
        // History
        history: [],
        historyCount: 0,
        historyPage: 0,
        historyPageSize: 20,
        historyUserId: "",
        historyFrom: "",
        historyTo: "",
        historyLoading: false,
        showHistory: false,
    };

    componentDidMount() {
        this.fetchTargets();
    }

    fetchTargets = async () => {
        this.setState({ loading: true });
        try {
            const res = await API.get("/admin/targets");
            const data = res.data.data || res.data;
            const { executives, targets, futureTargets } = data;

            // Find global target
            const globalTarget = targets.find((t) => t.user_id === null);
            const globalPosted = globalTarget ? globalTarget.posted_target : "";
            const globalAttempted = globalTarget ? globalTarget.attempted_target : "";

            // Build per-user form state
            const userForms = {};
            for (const exec of executives) {
                const userTarget = targets.find((t) => t.user_id === exec.id);
                userForms[exec.id] = {
                    posted: userTarget ? userTarget.posted_target : "",
                    attempted: userTarget ? userTarget.attempted_target : "",
                    effective: new Date().toISOString().slice(0, 10),
                    saving: false,
                    hasOverride: !!userTarget,
                    targetId: userTarget ? userTarget.id : null,
                };
            }

            this.setState({
                executives,
                targets,
                futureTargets,
                globalPosted,
                globalAttempted,
                userForms,
                loading: false,
            });
        } catch (e) {
            toast.error("Failed to load targets");
            this.setState({ loading: false });
        }
    };

    // ─── Global Target ───────────────────────────────────
    saveGlobalTarget = async () => {
        const { globalPosted, globalAttempted, globalEffective } = this.state;
        if (globalPosted === "" && globalAttempted === "") {
            return toast.error("Please enter at least one target value");
        }
        this.setState({ savingGlobal: true });
        try {
            await API.post("/admin/target", {
                user_id: null,
                posted_target: Number(globalPosted) || 0,
                attempted_target: Number(globalAttempted) || 0,
                effective_from: globalEffective,
            });
            toast.success("Global target saved");
            this.fetchTargets();
        } catch (e) {
            toast.error("Failed to save global target");
        }
        this.setState({ savingGlobal: false });
    };

    // ─── Per-User Target ─────────────────────────────────
    updateUserForm = (userId, field, value) => {
        this.setState((prev) => ({
            userForms: {
                ...prev.userForms,
                [userId]: { ...prev.userForms[userId], [field]: value },
            },
        }));
    };

    saveUserTarget = async (userId) => {
        const form = this.state.userForms[userId];
        if (form.posted === "" && form.attempted === "") {
            return toast.error("Please enter at least one target value");
        }
        this.updateUserForm(userId, "saving", true);
        try {
            await API.post("/admin/target", {
                user_id: userId,
                posted_target: Number(form.posted) || 0,
                attempted_target: Number(form.attempted) || 0,
                effective_from: form.effective,
            });
            toast.success("Target saved for user");
            this.fetchTargets();
        } catch (e) {
            toast.error("Failed to save target");
        }
        this.updateUserForm(userId, "saving", false);
    };

    deleteUserTarget = async (userId) => {
        const form = this.state.userForms[userId];
        if (!form.targetId) return;
        if (!window.confirm("Remove override for this user? They will fall back to the global default.")) return;
        this.updateUserForm(userId, "saving", true);
        try {
            await API.post("/admin/target/delete", { target_id: form.targetId });
            toast.success("Override removed. User will use global default.");
            this.fetchTargets();
        } catch (e) {
            toast.error(e.response && e.response.data ? e.response.data.message : "Failed to delete target");
        }
        this.updateUserForm(userId, "saving", false);
    };

    // ─── History ─────────────────────────────────────────
    fetchHistory = async () => {
        this.setState({ historyLoading: true });
        try {
            const { historyPage, historyPageSize, historyUserId, historyFrom, historyTo } = this.state;
            const params = { limit: historyPageSize, offset: historyPage * historyPageSize };
            if (historyUserId) params.user_id = historyUserId;
            if (historyFrom) params.from = historyFrom;
            if (historyTo) params.to = historyTo;

            const res = await API.get("/admin/target-history", { params });
            const data = res.data.data || res.data;
            this.setState({
                history: data.history || [],
                historyCount: data.count || 0,
                historyLoading: false,
            });
        } catch (e) {
            toast.error("Failed to load history");
            this.setState({ historyLoading: false });
        }
    };

    toggleHistory = () => {
        this.setState(
            (prev) => ({ showHistory: !prev.showHistory }),
            () => {
                if (this.state.showHistory && this.state.history.length === 0) {
                    this.fetchHistory();
                }
            }
        );
    };

    handleHistorySearch = (e) => {
        e.preventDefault();
        this.setState({ historyPage: 0 }, this.fetchHistory);
    };

    handleHistoryPageChange = (page) => {
        this.setState({ historyPage: page }, this.fetchHistory);
    };

    render() {
        const {
            executives, loading, globalPosted, globalAttempted, globalEffective, savingGlobal,
            userForms, futureTargets,
            showHistory, history, historyCount, historyPage, historyPageSize, historyLoading,
            historyUserId, historyFrom, historyTo,
        } = this.state;

        const historyTotalPages = Math.ceil(historyCount / historyPageSize);

        if (loading) {
            return (
                <div>
                    <h2 className="page-header"><i className="fas fa-bullseye"></i> Daily Targets</h2>
                    <p><i className="fas fa-spinner fa-spin"></i> Loading...</p>
                </div>
            );
        }

        return (
            <div>
                <h2 className="page-header"><i className="fas fa-bullseye"></i> Daily Targets</h2>

                {/* ── Global Default Target ── */}
                <div className="panel panel-primary">
                    <div className="panel-heading">
                        <h3 className="panel-title"><i className="fas fa-globe"></i> Global Default Target</h3>
                    </div>
                    <div className="panel-body">
                        <p className="text-muted" style={{ marginBottom: 15 }}>
                            This applies to all executives who don't have a per-user override.
                        </p>
                        <div className="row">
                            <div className="col-md-3">
                                <div className="form-group">
                                    <label>Posted Target (daily)</label>
                                    <input type="number" className="form-control" min="0"
                                        value={globalPosted}
                                        onChange={(e) => this.setState({ globalPosted: e.target.value })}
                                        placeholder="e.g. 10" />
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="form-group">
                                    <label>Attempted Target (daily)</label>
                                    <input type="number" className="form-control" min="0"
                                        value={globalAttempted}
                                        onChange={(e) => this.setState({ globalAttempted: e.target.value })}
                                        placeholder="e.g. 30" />
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="form-group">
                                    <label>Effective From</label>
                                    <input type="date" className="form-control"
                                        value={globalEffective}
                                        onChange={(e) => this.setState({ globalEffective: e.target.value })} />
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="form-group">
                                    <label>&nbsp;</label>
                                    <div>
                                        <button className="btn btn-primary" onClick={this.saveGlobalTarget}
                                            disabled={savingGlobal}>
                                            {savingGlobal ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-save"></i>}
                                            {" "}Save Global Target
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Per-User Targets ── */}
                <div className="panel panel-default">
                    <div className="panel-heading">
                        <h3 className="panel-title"><i className="fas fa-users"></i> Per-User Target Overrides</h3>
                    </div>
                    <div className="panel-body" style={{ padding: 0 }}>
                        <div className="table-responsive">
                            <table className="table table-bordered table-hover" style={{ margin: 0 }}>
                                <thead>
                                    <tr style={{ backgroundColor: "#f5f5f5" }}>
                                        <th>Executive</th>
                                        <th style={{ width: 130 }}>Posted Target</th>
                                        <th style={{ width: 130 }}>Attempted Target</th>
                                        <th style={{ width: 150 }}>Effective From</th>
                                        <th style={{ width: 200 }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {executives.map((exec) => {
                                        const form = userForms[exec.id] || {};
                                        return (
                                            <tr key={exec.id} style={form.hasOverride ? { backgroundColor: "#fdf8e8" } : {}}>
                                                <td>
                                                    <strong>{exec.name}</strong>
                                                    <br /><small className="text-muted">{exec.email}</small>
                                                    {form.hasOverride && (
                                                        <span className="label label-warning" style={{ marginLeft: 8 }}>Override</span>
                                                    )}
                                                </td>
                                                <td>
                                                    <input type="number" className="form-control input-sm" min="0"
                                                        value={form.posted}
                                                        onChange={(e) => this.updateUserForm(exec.id, "posted", e.target.value)}
                                                        placeholder={globalPosted || "0"} />
                                                </td>
                                                <td>
                                                    <input type="number" className="form-control input-sm" min="0"
                                                        value={form.attempted}
                                                        onChange={(e) => this.updateUserForm(exec.id, "attempted", e.target.value)}
                                                        placeholder={globalAttempted || "0"} />
                                                </td>
                                                <td>
                                                    <input type="date" className="form-control input-sm"
                                                        value={form.effective}
                                                        onChange={(e) => this.updateUserForm(exec.id, "effective", e.target.value)} />
                                                </td>
                                                <td>
                                                    <button className="btn btn-success btn-xs"
                                                        disabled={form.saving}
                                                        onClick={() => this.saveUserTarget(exec.id)}>
                                                        {form.saving ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-save"></i>}
                                                        {" "}Save
                                                    </button>
                                                    {form.hasOverride && (
                                                        <button className="btn btn-danger btn-xs" style={{ marginLeft: 5 }}
                                                            disabled={form.saving}
                                                            onClick={() => this.deleteUserTarget(exec.id)}>
                                                            <i className="fas fa-trash"></i> Remove
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    {executives.length === 0 && (
                                        <tr><td colSpan="5" className="text-center text-muted">No executives found</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* ── Future Targets ── */}
                {futureTargets.length > 0 && (
                    <div className="panel panel-info">
                        <div className="panel-heading">
                            <h3 className="panel-title"><i className="fas fa-calendar-alt"></i> Upcoming Scheduled Targets</h3>
                        </div>
                        <div className="panel-body" style={{ padding: 0 }}>
                            <table className="table table-bordered" style={{ margin: 0 }}>
                                <thead>
                                    <tr>
                                        <th>User</th>
                                        <th>Posted Target</th>
                                        <th>Attempted Target</th>
                                        <th>Effective From</th>
                                        <th>Set By</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {futureTargets.map((ft) => {
                                        const userName = ft.user_id
                                            ? (executives.find((e) => e.id === ft.user_id) || {}).name || `User #${ft.user_id}`
                                            : "Global Default";
                                        return (
                                            <tr key={ft.id}>
                                                <td>{userName}</td>
                                                <td>{ft.posted_target}</td>
                                                <td>{ft.attempted_target}</td>
                                                <td>{ft.effective_from}</td>
                                                <td>{ft.created_by_name || "-"}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* ── Change History ── */}
                <div className="panel panel-default">
                    <div className="panel-heading" style={{ cursor: "pointer" }} onClick={this.toggleHistory}>
                        <h3 className="panel-title">
                            <i className={`fas fa-chevron-${showHistory ? "down" : "right"}`}></i>
                            {" "}Target Change History
                        </h3>
                    </div>
                    {showHistory && (
                        <div className="panel-body">
                            {/* Filters */}
                            <form onSubmit={this.handleHistorySearch} className="form-inline" style={{ marginBottom: 15 }}>
                                <div className="form-group" style={{ marginRight: 10 }}>
                                    <select className="form-control input-sm"
                                        value={historyUserId}
                                        onChange={(e) => this.setState({ historyUserId: e.target.value })}>
                                        <option value="">All Users</option>
                                        <option value="global">Global Default</option>
                                        {executives.map((u) => (
                                            <option key={u.id} value={u.id}>{u.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group" style={{ marginRight: 10 }}>
                                    <label style={{ marginRight: 5, fontSize: 12 }}>From</label>
                                    <input type="date" className="form-control input-sm"
                                        value={historyFrom}
                                        onChange={(e) => this.setState({ historyFrom: e.target.value })} />
                                </div>
                                <div className="form-group" style={{ marginRight: 10 }}>
                                    <label style={{ marginRight: 5, fontSize: 12 }}>To</label>
                                    <input type="date" className="form-control input-sm"
                                        value={historyTo}
                                        onChange={(e) => this.setState({ historyTo: e.target.value })} />
                                </div>
                                <button type="submit" className="btn btn-primary btn-sm">
                                    <i className="fas fa-search"></i> Filter
                                </button>
                            </form>

                            {historyLoading ? (
                                <p><i className="fas fa-spinner fa-spin"></i> Loading history...</p>
                            ) : (
                                <div>
                                    <table className="table table-striped table-bordered table-condensed">
                                        <thead>
                                            <tr>
                                                <th>Date</th>
                                                <th>User</th>
                                                <th>Action</th>
                                                <th>Posted (Old → New)</th>
                                                <th>Attempted (Old → New)</th>
                                                <th>Changed By</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {history.length === 0 ? (
                                                <tr><td colSpan="6" className="text-center text-muted">No history found</td></tr>
                                            ) : (
                                                history.map((h) => (
                                                    <tr key={h.id}>
                                                        <td style={{ whiteSpace: "nowrap" }}>{h.changed_at}</td>
                                                        <td>{h.user_name || "Global Default"}</td>
                                                        <td>
                                                            <span className={`label label-${h.action === "created" ? "success" : h.action === "updated" ? "info" : "danger"}`}>
                                                                {h.action}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            {h.posted_target_old !== null ? h.posted_target_old : "-"}
                                                            {" → "}
                                                            {h.posted_target_new !== null ? h.posted_target_new : "-"}
                                                        </td>
                                                        <td>
                                                            {h.attempted_target_old !== null ? h.attempted_target_old : "-"}
                                                            {" → "}
                                                            {h.attempted_target_new !== null ? h.attempted_target_new : "-"}
                                                        </td>
                                                        <td>{h.changed_by_name || "-"}</td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>

                                    {historyTotalPages > 1 && (
                                        <nav className="text-center">
                                            <ul className="pagination pagination-sm">
                                                <li className={historyPage === 0 ? "disabled" : ""}>
                                                    <a href="#prev" onClick={(e) => { e.preventDefault(); if (historyPage > 0) this.handleHistoryPageChange(historyPage - 1); }}>&laquo;</a>
                                                </li>
                                                {Array.from({ length: Math.min(historyTotalPages, 10) }, (_, i) => {
                                                    let p = historyTotalPages <= 10 ? i : Math.max(0, Math.min(historyPage - 4, historyTotalPages - 10)) + i;
                                                    return (
                                                        <li key={p} className={historyPage === p ? "active" : ""}>
                                                            <a href={`#page-${p}`} onClick={(e) => { e.preventDefault(); this.handleHistoryPageChange(p); }}>{p + 1}</a>
                                                        </li>
                                                    );
                                                })}
                                                <li className={historyPage >= historyTotalPages - 1 ? "disabled" : ""}>
                                                    <a href="#next" onClick={(e) => { e.preventDefault(); if (historyPage < historyTotalPages - 1) this.handleHistoryPageChange(historyPage + 1); }}>&raquo;</a>
                                                </li>
                                            </ul>
                                        </nav>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    }
}

export default AdminTargetsPage;
